import { getErrorMessage } from '@/api/errors'
import { isFieldVisible } from '@/components/projects/workflows/chat/TriggerField'
import { buildDefaults } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import { matchesWorkflowSearch } from '@/components/projects/workflows/shared/workflowSearch'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useAuth,
  useContextSources,
  useCreateWorkflowSchedule,
  usePauseWorkflowSchedule,
  useResumeWorkflowSchedule,
  useUpdateWorkflowSchedule,
  useWorkflowDefinition,
  useWorkflowDefinitions,
  useWorkflowTriggerSchema,
} from '@/hooks'
import { useDebouncedWorkflowSchedulePreview } from '@/hooks/useWorkflowSchedules'
import { cn } from '@/lib/utils'
import { extractValidationErrors, extractValidationMessage } from '@/lib/validationErrors'
import {
  type ContextSourceListItem,
  type WorkflowSchedule,
  type WorkflowScheduleRecurrenceKind,
  type WorkflowScheduleRecurrenceRequest,
} from '@/types'
import { ArrowLeft, ArrowRight, CalendarClock, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { ScheduleInputsStep } from './ScheduleInputsStep'
import { ScheduleRecurrencePreview } from './ScheduleRecurrencePreview'
import { getBrowserTimeZone, getSupportedTimeZones } from './scheduleFormat'
import { TimeZonePicker } from './TimeZonePicker'

const NAME_MAX_LENGTH = 200
const DESCRIPTION_MAX_LENGTH = 1000
const CRON_MAX_LENGTH = 120
const ACTIVE_DEFINITIONS_FILTERS = { search: '', status: 'active', type: 'all', category: 'all' } as const
const ALL_DEFINITIONS_PAGINATION = { skipCount: 0, maxResultCount: 1000 }
const OWNER_SERVER_FIELD = 'OwnerUserId'
const SAVE_FAILED_MESSAGE = 'The schedule could not be saved.'
const STATE_CHANGE_FAILED_MESSAGE = 'the request failed.'
const PREVIEW_ERROR_TYPE = 'preview'
const RECURRENCE_KIND_OPTIONS = [['Cron', 'Cron'], ['Interval', 'Every N minutes']] as const
const NEXT_RADIO_KEYS = new Set(['ArrowRight', 'ArrowDown'])
const PREVIOUS_RADIO_KEYS = new Set(['ArrowLeft', 'ArrowUp'])

type Step = 'schedule' | 'inputs'

interface ScheduleFormValues {
  name: string
  description: string
  targetDefinitionId: string
  recurrenceKind: WorkflowScheduleRecurrenceKind
  cronExpression: string
  intervalMinutes: string
  timeZoneId: string
  active: boolean
}

const SCHEDULE_FIELD_BY_SERVER_FIELD = new Map<string, Exclude<keyof ScheduleFormValues, 'active'>>([
  ['Name', 'name'],
  ['Description', 'description'],
  ['TargetDefinitionId', 'targetDefinitionId'],
  ['RecurrenceKind', 'recurrenceKind'],
  ['CronExpression', 'cronExpression'],
  ['IntervalMinutes', 'intervalMinutes'],
  ['TimeZoneId', 'timeZoneId'],
])

const RECURRENCE_FIELDS = ['recurrenceKind', 'cronExpression', 'intervalMinutes', 'timeZoneId'] as const
type RecurrenceField = typeof RECURRENCE_FIELDS[number]
type RecurrenceFieldErrors = ReadonlyMap<RecurrenceField, string>

const NO_RECURRENCE_FIELD_ERRORS: RecurrenceFieldErrors = new Map()

function isRecurrenceField(field: string): field is RecurrenceField {
  return (RECURRENCE_FIELDS as readonly string[]).includes(field)
}

function toRecurrenceFieldErrors(error: unknown): RecurrenceFieldErrors {
  const byField = new Map<RecurrenceField, string>()
  for (const { field, errorMessage } of extractValidationErrors(error) ?? []) {
    const formField = SCHEDULE_FIELD_BY_SERVER_FIELD.get(field)
    if (errorMessage && formField && isRecurrenceField(formField) && !byField.has(formField)) {
      byField.set(formField, errorMessage)
    }
  }
  return byField.size > 0 ? byField : NO_RECURRENCE_FIELD_ERRORS
}

interface ScheduleFormDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule?: WorkflowSchedule | null
  onSaved?: (scheduleId: string) => void
}

function toFormValues(schedule: WorkflowSchedule | null | undefined): ScheduleFormValues {
  return {
    name: schedule?.name ?? '',
    description: schedule?.description ?? '',
    targetDefinitionId: schedule?.targetDefinitionId ?? '',
    recurrenceKind: schedule?.recurrenceKind ?? 'Cron',
    cronExpression: schedule?.cronExpression ?? '0 9 * * 1-5',
    intervalMinutes: String(schedule?.intervalMinutes ?? 60),
    timeZoneId: schedule?.timeZoneId ?? getBrowserTimeZone(),
    active: schedule ? schedule.state === 'Active' : true,
  }
}

function parseStoredInputs(json: string | undefined): FormValues {
  if (!json) return {}
  try {
    const parsed: unknown = JSON.parse(json)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([key, value]) => [
        key,
        typeof value === 'string' ? value : JSON.stringify(value),
      ]),
    )
  } catch {
    return {}
  }
}

function parseInterval(value: string): number | null {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null
  return Number(trimmed)
}

function buildRecurrence(
  values: Pick<ScheduleFormValues, 'recurrenceKind' | 'cronExpression' | 'intervalMinutes' | 'timeZoneId'>,
  schedule: WorkflowSchedule | null | undefined,
): WorkflowScheduleRecurrenceRequest | null {
  const timeZoneId = values.timeZoneId.trim()
  if (!timeZoneId) return null

  if (values.recurrenceKind === 'Interval') {
    const intervalMinutes = parseInterval(values.intervalMinutes)
    if (intervalMinutes === null) return null
    const anchorUtc = schedule?.recurrenceKind === 'Interval' && schedule.intervalMinutes === intervalMinutes
      ? schedule.anchorUtc
      : null
    return { recurrenceKind: 'Interval', intervalMinutes, cronExpression: null, anchorUtc, timeZoneId }
  }

  const cronExpression = values.cronExpression.trim()
  if (!cronExpression) return null
  return { recurrenceKind: 'Cron', cronExpression, intervalMinutes: null, anchorUtc: null, timeZoneId }
}

export function ScheduleFormDialog({ projectId, open, onOpenChange, schedule, onSaved }: ScheduleFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="max-w-[760px] rounded-2xl p-0 shadow-elevated">
        <ScheduleFormBody projectId={projectId} schedule={schedule} onClose={() => onOpenChange(false)} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  )
}

interface ScheduleFormBodyProps {
  projectId: string
  schedule: WorkflowSchedule | null | undefined
  onClose: () => void
  onSaved?: (scheduleId: string) => void
}

function ScheduleFormBody({ projectId, schedule, onClose, onSaved }: ScheduleFormBodyProps) {
  const isEdit = !!schedule
  const [step, setStep] = useState<Step>('schedule')
  const [workflowSearch, setWorkflowSearch] = useState('')
  const [saveErrors, setSaveErrors] = useState<string[]>([])
  const timeZones = useMemo(() => getSupportedTimeZones(), [])
  const { user } = useAuth(projectId)
  const recurrenceKindRefs = useRef<Partial<Record<WorkflowScheduleRecurrenceKind, HTMLButtonElement | null>>>({})

  const [initialValues] = useState(() => toFormValues(schedule))
  const scheduleForm = useForm<ScheduleFormValues>({ mode: 'onBlur', defaultValues: initialValues })
  const inputsForm = useForm<FormValues>({ mode: 'onBlur' })
  const { control, register, handleSubmit, setValue, setError, clearErrors, formState: { errors } } = scheduleForm
  const { reset: resetInputs } = inputsForm

  const [targetDefinitionId, recurrenceKind, cronExpression, intervalMinutes, timeZoneId] = useWatch({
    control,
    name: ['targetDefinitionId', 'recurrenceKind', 'cronExpression', 'intervalMinutes', 'timeZoneId'],
  })

  const { data: definitionsPage, isLoading: definitionsLoading } = useWorkflowDefinitions(
    projectId,
    ALL_DEFINITIONS_PAGINATION,
    ACTIVE_DEFINITIONS_FILTERS,
  )
  const { data: storedTarget } = useWorkflowDefinition(projectId, schedule?.targetDefinitionId ?? '')

  const definitionOptions = useMemo(() => {
    const options = (definitionsPage?.items ?? []).map(d => ({
      id: d.id,
      name: d.name,
      displayName: d.displayName,
      description: d.description,
    }))
    if (schedule && !options.some(o => o.id === schedule.targetDefinitionId)) {
      options.push({
        id: schedule.targetDefinitionId,
        name: storedTarget?.name ?? '',
        displayName: storedTarget?.displayName ?? schedule.targetName ?? 'Deleted workflow',
        description: null,
      })
    }
    return options.sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [definitionsPage, schedule, storedTarget])

  const selectedDefinition = definitionOptions.find(o => o.id === targetDefinitionId) ?? null
  const matchingDefinitionOptions = definitionOptions.filter(o => matchesWorkflowSearch(o, workflowSearch))

  const {
    data: schema,
    isLoading: schemaLoading,
    isError: schemaError,
  } = useWorkflowTriggerSchema(projectId, selectedDefinition?.name || null)
  const inputFields = useMemo(() => schema?.fields ?? [], [schema])

  const hasContextPickerMultiSelect = useMemo(
    () => inputFields.some(f => f.type === 'context-picker' && f.multiSelect),
    [inputFields],
  )
  const { data: contextData, isLoading: contextLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: hasContextPickerMultiSelect },
  )
  const contextItems: ContextSourceListItem[] = useMemo(() => contextData?.items ?? [], [contextData])

  const appliedInputsKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (appliedInputsKeyRef.current !== targetDefinitionId) appliedInputsKeyRef.current = null
  }, [targetDefinitionId])

  useEffect(() => {
    if (step !== 'inputs' || !targetDefinitionId || !schema) return
    if (hasContextPickerMultiSelect && contextLoading) return
    if (appliedInputsKeyRef.current === targetDefinitionId) return

    appliedInputsKeyRef.current = targetDefinitionId
    const defaults = buildDefaults(inputFields, contextItems)
    const stored = schedule && schedule.targetDefinitionId === targetDefinitionId
      ? parseStoredInputs(schedule.inputValuesJson)
      : {}
    resetInputs({ ...defaults, ...stored })
  }, [step, targetDefinitionId, schema, inputFields, hasContextPickerMultiSelect, contextLoading, contextItems, schedule, resetInputs])

  const recurrenceRequest = useMemo(
    () => buildRecurrence({ recurrenceKind, cronExpression, intervalMinutes, timeZoneId }, schedule),
    [recurrenceKind, cronExpression, intervalMinutes, timeZoneId, schedule],
  )
  const preview = useDebouncedWorkflowSchedulePreview(projectId, recurrenceRequest)
  const previewFieldErrors = useMemo(
    () => (preview.isError && !preview.isSettling ? toRecurrenceFieldErrors(preview.error) : NO_RECURRENCE_FIELD_ERRORS),
    [preview.isError, preview.isSettling, preview.error],
  )
  const hasPreviewFieldErrors = previewFieldErrors.size > 0

  useEffect(() => {
    const current = {
      recurrenceKind: errors.recurrenceKind,
      cronExpression: errors.cronExpression,
      intervalMinutes: errors.intervalMinutes,
      timeZoneId: errors.timeZoneId,
    }
    for (const field of RECURRENCE_FIELDS) {
      const message = previewFieldErrors.get(field)
      const error = current[field]
      if (message) {
        if (!error || (error.type === PREVIEW_ERROR_TYPE && error.message !== message)) {
          setError(field, { type: PREVIEW_ERROR_TYPE, message })
        }
      } else if (error?.type === PREVIEW_ERROR_TYPE) {
        clearErrors(field)
      }
    }
  }, [previewFieldErrors, errors.recurrenceKind, errors.cronExpression, errors.intervalMinutes, errors.timeZoneId, setError, clearErrors])

  const createSchedule = useCreateWorkflowSchedule(projectId)
  const updateSchedule = useUpdateWorkflowSchedule(projectId)
  const pauseSchedule = usePauseWorkflowSchedule(projectId)
  const resumeSchedule = useResumeWorkflowSchedule(projectId)
  const isSaving = createSchedule.isPending || updateSchedule.isPending || pauseSchedule.isPending || resumeSchedule.isPending

  const ownerName = schedule
    ? schedule.ownerDisplayName ?? 'the schedule owner'
    : user?.displayName ?? 'you'

  const goToInputs = handleSubmit(() => {
    setSaveErrors([])
    setStep('inputs')
  })

  const handleSave = inputsForm.handleSubmit(async (inputValues) => {
    const values = scheduleForm.getValues()
    const recurrence = buildRecurrence(values, schedule)
    if (!recurrence) {
      setStep('schedule')
      return
    }

    const visibleFields = inputFields.filter(f => isFieldVisible(f, inputValues as Record<string, unknown>))
    const visibleFieldNames = new Set(visibleFields.map(f => f.name))
    const inputValuesJson = JSON.stringify(
      Object.fromEntries(Object.entries(inputValues).filter(([key]) => visibleFieldNames.has(key))),
    )
    const name = values.name.trim()
    const description = values.description.trim() || null

    setSaveErrors([])
    try {
      if (schedule) {
        await updateSchedule.mutateAsync({
          scheduleId: schedule.id,
          data: { ...recurrence, name, description, inputValuesJson },
        })
        if (values.active !== initialValues.active) {
          try {
            await (values.active ? resumeSchedule : pauseSchedule).mutateAsync(schedule.id)
          } catch (stateErr) {
            const reason = extractValidationMessage(stateErr) ?? getErrorMessage(stateErr) ?? STATE_CHANGE_FAILED_MESSAGE
            toast.error(`Saved, but the schedule could not be ${values.active ? 'resumed' : 'paused'}: ${reason}`)
            onSaved?.(schedule.id)
            onClose()
            return
          }
        }
        toast.success(`Schedule "${name}" updated`)
        onSaved?.(schedule.id)
      } else {
        const created = await createSchedule.mutateAsync({
          ...recurrence,
          name,
          description,
          targetDefinitionId: values.targetDefinitionId,
          inputValuesJson,
          active: values.active,
        })
        toast.success(`Schedule "${name}" created`)
        onSaved?.(created.id)
      }
      onClose()
    } catch (err) {
      const validationErrors = extractValidationErrors(err) ?? []
      const displayedInputNames = new Set(visibleFields.filter(f => !f.hidden).map(f => f.name))
      const unplaced: string[] = []
      let placedOnScheduleStep = false
      let placedOnInputsStep = false

      for (const { field, errorMessage } of validationErrors) {
        if (!errorMessage) continue
        const scheduleField = SCHEDULE_FIELD_BY_SERVER_FIELD.get(field)
        if (scheduleField) {
          scheduleForm.setError(scheduleField, { type: 'server', message: errorMessage })
          placedOnScheduleStep = true
        } else if (field === OWNER_SERVER_FIELD) {
          scheduleForm.setError('root.owner', { type: 'server', message: errorMessage })
          placedOnScheduleStep = true
        } else if (displayedInputNames.has(field)) {
          inputsForm.setError(field, { type: 'server', message: errorMessage })
          placedOnInputsStep = true
        } else {
          unplaced.push(errorMessage)
        }
      }

      if (!placedOnScheduleStep && !placedOnInputsStep && unplaced.length === 0) {
        unplaced.push(getErrorMessage(err) ?? SAVE_FAILED_MESSAGE)
      }
      setSaveErrors(unplaced)
      if (placedOnScheduleStep) setStep('schedule')
    }
  })

  const handleRecurrenceKindKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
    onChange: (kind: WorkflowScheduleRecurrenceKind) => void,
  ) => {
    const delta = NEXT_RADIO_KEYS.has(event.key) ? 1 : PREVIOUS_RADIO_KEYS.has(event.key) ? -1 : 0
    if (delta === 0) return
    event.preventDefault()
    const count = RECURRENCE_KIND_OPTIONS.length
    const [nextKind] = RECURRENCE_KIND_OPTIONS[(index + delta + count) % count]
    onChange(nextKind)
    recurrenceKindRefs.current[nextKind]?.focus()
  }

  const title = isEdit ? 'Edit schedule' : 'New schedule'
  const workflowDisplayName = selectedDefinition?.displayName ?? 'this workflow'

  return (
    <form
      onSubmit={step === 'inputs' ? handleSave : goToInputs}
      className="flex flex-col max-h-[85vh] overflow-hidden"
      noValidate
      data-testid="schedule-form"
    >
      <div className="shrink-0 px-8 py-6 border-b space-y-3">
        <div className="flex items-center justify-between">
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            <CalendarClock className="h-6 w-6 shrink-0" aria-hidden="true" />
            {title}
          </DialogTitle>
          <DialogClose
            className="rounded-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </DialogClose>
        </div>
        <DialogDescription className="sr-only">
          Step 1 sets when the workflow runs; step 2 sets the inputs stored with the schedule.
        </DialogDescription>
        <ol className="flex items-center gap-2 text-sm" aria-label="Schedule form steps">
          <li
            aria-current={step === 'schedule' ? 'step' : undefined}
            className={cn('rounded-full px-3 py-1', step === 'schedule' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
          >
            1. Schedule
          </li>
          <li aria-hidden="true" className="text-muted-foreground">›</li>
          <li
            aria-current={step === 'inputs' ? 'step' : undefined}
            className={cn('rounded-full px-3 py-1', step === 'inputs' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
          >
            2. Inputs
          </li>
        </ol>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-8 py-6 space-y-6">
        {step === 'schedule' ? (
          <div className="space-y-5" data-testid="schedule-step">
            {errors.root?.owner && (
              <div
                className="rounded-lg border border-danger-foreground bg-danger px-4 py-3 text-sm text-danger-foreground"
                role="alert"
                data-testid="schedule-owner-error"
              >
                {errors.root.owner.message}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="schedule-name">Name <span className="text-red-500" aria-hidden="true">*</span></Label>
              <Input
                id="schedule-name"
                aria-required="true"
                maxLength={NAME_MAX_LENGTH}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'schedule-name-error' : undefined}
                {...register('name', {
                  validate: v => v.trim().length > 0 || 'Name is required',
                  maxLength: { value: NAME_MAX_LENGTH, message: `Name must be at most ${NAME_MAX_LENGTH} characters` },
                })}
              />
              {errors.name && <span id="schedule-name-error" role="alert" className="text-xs text-danger-foreground">{errors.name.message}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="schedule-description">Description (optional)</Label>
              <Textarea
                id="schedule-description"
                rows={2}
                maxLength={DESCRIPTION_MAX_LENGTH}
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? 'schedule-description-error' : undefined}
                {...register('description', {
                  maxLength: { value: DESCRIPTION_MAX_LENGTH, message: `Description must be at most ${DESCRIPTION_MAX_LENGTH} characters` },
                })}
              />
              {errors.description && <span id="schedule-description-error" role="alert" className="text-xs text-danger-foreground">{errors.description.message}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="schedule-workflow">Workflow <span className="text-red-500" aria-hidden="true">*</span></Label>
              <Controller
                control={control}
                name="targetDefinitionId"
                rules={{ required: 'Pick a workflow' }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={value => { if (value) field.onChange(value) }}
                    onOpenChange={isOpen => { if (!isOpen) setWorkflowSearch('') }}
                    disabled={isEdit || definitionsLoading}
                  >
                    <SelectTrigger
                      id="schedule-workflow"
                      aria-label="Workflow"
                      aria-required="true"
                      aria-invalid={!!errors.targetDefinitionId}
                      aria-describedby="schedule-workflow-hint"
                    >
                      {/* Explicit children: Radix otherwise blanks the trigger while the search filters out the selected item. */}
                      <SelectValue placeholder={definitionsLoading ? 'Loading workflows…' : 'Select a workflow'}>
                        {selectedDefinition?.displayName}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <div className="sticky top-0 z-10 bg-popover pb-1">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                          <Input
                            value={workflowSearch}
                            onChange={e => setWorkflowSearch(e.target.value)}
                            // Radix Select treats typed keys as item typeahead; only arrows may reach it, to move into the list.
                            onKeyDown={e => { if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') e.stopPropagation() }}
                            placeholder="Search by name or description"
                            aria-label="Search workflows"
                            className="h-8 pl-8"
                          />
                        </div>
                      </div>
                      {matchingDefinitionOptions.length === 0 ? (
                        <p className="px-2 py-4 text-center text-sm text-muted-foreground">No workflows match your search.</p>
                      ) : (
                        matchingDefinitionOptions.map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.displayName}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.targetDefinitionId ? (
                <span id="schedule-workflow-hint" role="alert" className="text-xs text-danger-foreground">{errors.targetDefinitionId.message}</span>
              ) : (
                <span id="schedule-workflow-hint" className="text-xs text-muted-foreground">
                  {isEdit
                    ? 'The workflow of an existing schedule cannot be changed. Create a new schedule to run another workflow.'
                    : !definitionsLoading && definitionOptions.length === 0
                      ? 'There are no active workflows in this project.'
                      : 'Only active workflows can be scheduled.'}
                </span>
              )}
            </div>

            <fieldset className="flex flex-col gap-3">
              <legend className="mb-1.5 text-sm font-medium">Recurrence</legend>
              <Controller
                control={control}
                name="recurrenceKind"
                render={({ field }) => (
                  <div
                    className="flex gap-1 rounded-lg border border-border p-1 w-fit"
                    role="radiogroup"
                    aria-label="Recurrence type"
                    aria-invalid={!!errors.recurrenceKind}
                    aria-describedby={errors.recurrenceKind ? 'schedule-recurrence-kind-error' : undefined}
                  >
                    {RECURRENCE_KIND_OPTIONS.map(([kind, label], index) => (
                      <button
                        key={kind}
                        ref={element => { recurrenceKindRefs.current[kind] = element }}
                        type="button"
                        role="radio"
                        aria-checked={field.value === kind}
                        tabIndex={field.value === kind ? 0 : -1}
                        onClick={() => field.onChange(kind)}
                        onKeyDown={event => handleRecurrenceKindKeyDown(event, index, field.onChange)}
                        className={cn(
                          'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                          field.value === kind ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              />
              {errors.recurrenceKind && (
                <span id="schedule-recurrence-kind-error" role="alert" className="text-xs text-danger-foreground">{errors.recurrenceKind.message}</span>
              )}

              {recurrenceKind === 'Interval' ? (
                <div key="interval" className="flex flex-col gap-1.5 max-w-xs">
                  <Label htmlFor="schedule-interval">Every (minutes) <span className="text-red-500" aria-hidden="true">*</span></Label>
                  <Input
                    id="schedule-interval"
                    aria-required="true"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    aria-invalid={!!errors.intervalMinutes}
                    aria-describedby="schedule-interval-hint"
                    onWheel={e => e.currentTarget.blur()}
                    {...register('intervalMinutes', {
                      validate: v => {
                        if (recurrenceKind !== 'Interval') return true
                        const parsed = parseInterval(v)
                        return (parsed !== null && parsed >= 1) || 'Enter a whole number of minutes'
                      },
                    })}
                  />
                  {errors.intervalMinutes ? (
                    <span id="schedule-interval-hint" role="alert" className="text-xs text-danger-foreground">{errors.intervalMinutes.message}</span>
                  ) : (
                    <span id="schedule-interval-hint" className="text-xs text-muted-foreground">
                      Counted in elapsed UTC time, so daylight-saving changes do not shift it.
                    </span>
                  )}
                </div>
              ) : (
                <div key="cron" className="flex flex-col gap-1.5 max-w-md">
                  <Label htmlFor="schedule-cron">Cron expression <span className="text-red-500" aria-hidden="true">*</span></Label>
                  <Input
                    id="schedule-cron"
                    aria-required="true"
                    className="font-mono"
                    maxLength={CRON_MAX_LENGTH}
                    placeholder="0 9 * * 1-5"
                    aria-invalid={!!errors.cronExpression}
                    aria-describedby="schedule-cron-hint"
                    {...register('cronExpression', {
                      validate: v => recurrenceKind !== 'Cron' || v.trim().length > 0 || 'Cron expression is required',
                      maxLength: { value: CRON_MAX_LENGTH, message: `Cron expression must be at most ${CRON_MAX_LENGTH} characters` },
                    })}
                  />
                  {errors.cronExpression ? (
                    <span id="schedule-cron-hint" role="alert" className="text-xs text-danger-foreground">{errors.cronExpression.message}</span>
                  ) : (
                    <span id="schedule-cron-hint" className="text-xs text-muted-foreground">
                      Five fields — minute, hour, day of month, month, day of week — read as wall-clock time in the time zone below.
                    </span>
                  )}
                </div>
              )}
            </fieldset>

            <div className="flex flex-col gap-1.5 max-w-md">
              <Label htmlFor="schedule-timezone">Time zone <span className="text-red-500" aria-hidden="true">*</span></Label>
              <Controller
                control={control}
                name="timeZoneId"
                rules={{
                  validate: v => {
                    const trimmed = v.trim()
                    if (!trimmed) return 'Time zone is required'
                    return timeZones.includes(trimmed) || trimmed === schedule?.timeZoneId || 'Pick a time zone from the list'
                  },
                }}
                render={({ field }) => (
                  <TimeZonePicker
                    ref={field.ref}
                    id="schedule-timezone"
                    value={field.value}
                    timeZones={timeZones}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    invalid={!!errors.timeZoneId}
                    aria-describedby="schedule-timezone-hint"
                  />
                )}
              />
              {errors.timeZoneId ? (
                <span id="schedule-timezone-hint" role="alert" className="text-xs text-danger-foreground">{errors.timeZoneId.message}</span>
              ) : (
                <span id="schedule-timezone-hint" className="text-xs text-muted-foreground">
                  Search or browse IANA time zones. Defaults to your browser's time zone.{' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setValue('timeZoneId', getBrowserTimeZone(), { shouldValidate: true })}
                  >
                    Use {getBrowserTimeZone()}
                  </button>
                </span>
              )}
            </div>

            <ScheduleRecurrencePreview request={recurrenceRequest} preview={preview} hasFieldErrors={hasPreviewFieldErrors} />

            <p className="text-xs text-muted-foreground" data-testid="schedule-catch-up-note">
              If the platform is unavailable, the most recent missed run within 24 h is caught up automatically.
            </p>

            <div className="flex items-center justify-between rounded-lg border bg-page px-4 py-3">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="schedule-active" className="text-sm font-medium">Active</Label>
                <span className="text-xs text-muted-foreground">A paused schedule does not fire, and nothing is reported missed while it is paused.</span>
              </div>
              <Controller
                control={control}
                name="active"
                render={({ field }) => (
                  <Switch id="schedule-active" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>

            <p className="text-sm text-muted-foreground" data-testid="schedule-runs-as">
              Runs as <span className="font-medium text-foreground">{ownerName}</span> · owner
            </p>
          </div>
        ) : (
          <ScheduleInputsStep
            projectId={projectId}
            workflowDisplayName={workflowDisplayName}
            fields={inputFields}
            connectors={schema?.connectors ?? []}
            form={inputsForm}
            isLoading={schemaLoading || (hasContextPickerMultiSelect && contextLoading)}
            isError={schemaError || !selectedDefinition?.name}
          />
        )}
      </div>

      {saveErrors.length > 0 && (
        <div className="shrink-0 px-8 pt-3" role="alert" data-testid="schedule-save-errors">
          <ul className="space-y-1 text-sm text-danger-foreground">
            {saveErrors.map(message => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="shrink-0 flex justify-end gap-2 px-8 pt-4 pb-4 border-t border-border">
        {step === 'schedule' ? (
          <>
            <Button key="cancel" type="button" variant="ghost" className="h-10 rounded-lg px-4 font-bold" onClick={onClose}>
              Cancel
            </Button>
            <Button
              key="next"
              type="submit"
              className="h-10 rounded-lg px-4 gap-2 font-bold"
              disabled={!targetDefinitionId || hasPreviewFieldErrors}
              data-testid="schedule-next-button"
            >
              Next: Inputs
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </>
        ) : (
          <>
            <Button
              key="back"
              type="button"
              variant="ghost"
              className="h-10 rounded-lg px-4 gap-2 font-bold"
              onClick={() => setStep('schedule')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back
            </Button>
            <Button
              key="save"
              type="submit"
              className="h-10 rounded-lg px-4 font-bold"
              disabled={isSaving || schemaLoading || schemaError || !selectedDefinition?.name}
              data-testid="schedule-save-button"
            >
              {isSaving ? 'Saving...' : 'Save schedule'}
            </Button>
          </>
        )}
      </div>
    </form>
  )
}
